// ============================================================================
// FSA OPENING BALANCE VERIFICATION — CLIENT TB UPLOAD MODAL  (ADR-0007)
// ============================================================================
// TB-upload-style wizard for the client's CY opening TB: choose file →
// server preview (reuses the TB parser preview endpoint) → confirm the
// column mapping (Ledger / Group / Amount or Debit+Credit / Opening) →
// upload. The mapping travels to the verification upload endpoint exactly
// as the TB wizard sends it (columnMap JSON form field).
//
// Verification-only: this upload never enters the TB consolidation pipeline.

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import IconSystem from 'icon-collection/icon-systems';
import { SyutSelect } from 'components/ui';
import notificationService from 'services/notificationService';
import { fsaTrialBalanceService } from 'api/services/fsa/fsaTrialBalanceService';
import { fsaObVerificationService } from 'api/services/fsa/fsaObVerificationService';
import type { ID } from 'api/types';
import type {
  TrialBalanceColumnMap,
  TrialBalancePreviewResponse,
} from 'api/types/fsa/trialBalance.types';
import type { ObVerificationWorkspace } from 'api/types/fsa/obVerification.types';

interface Props {
  isOpen: boolean;
  engagementId: ID;
  onUploaded: (workspace: ObVerificationWorkspace) => void;
  onClose: () => void;
}

const EMPTY_MAP: TrialBalanceColumnMap = {
  ledgerIndex: -1, amountIndex: -1, debitIndex: -1, creditIndex: -1,
  groupIndex: -1, noteIndex: -1, classificationIndex: -1, openingIndex: -1,
  headerRowIndex: -1,
};

const sourceFromFileName = (name: string): string => {
  const ext = name.toLowerCase().split('.').pop() ?? '';
  if (ext === 'xml') return 'TALLY_XML';
  if (ext === 'csv' || ext === 'txt') return 'CSV';
  return 'EXCEL';
};

const fmt = (v: number): string =>
  v.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const FsaObUploadModal: React.FC<Props> = ({ isOpen, engagementId, onUploaded, onClose }) => {
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const [file, setFile] = useState<File | null>(null);
  const [source, setSource] = useState<string>('EXCEL');
  const [preview, setPreview] = useState<TrialBalancePreviewResponse | null>(null);
  const [columnMap, setColumnMap] = useState<TrialBalanceColumnMap>(EMPTY_MAP);
  const [previewing, setPreviewing] = useState(false);
  const [uploading, setUploading] = useState(false);

  useEffect(() => {
    if (!isOpen) return;
    setFile(null);
    setPreview(null);
    setColumnMap(EMPTY_MAP);
    if (fileInputRef.current) fileInputRef.current.value = '';
  }, [isOpen]);

  const runPreview = useCallback(async (f: File, src: string, map: TrialBalanceColumnMap | null) => {
    setPreviewing(true);
    try {
      const r = await fsaTrialBalanceService.preview(f, src as any, map);
      const p = r.data ?? null;
      setPreview(p);
      if (p?.effectiveMap) setColumnMap(p.effectiveMap);
    } catch (err: any) {
      notificationService.toast.error(err?.response?.data?.message ?? 'Could not parse the file.');
      setPreview(null);
    } finally {
      setPreviewing(false);
    }
  }, []);

  const handleFileChosen = useCallback((f: File | null) => {
    if (!f) return;
    const src = sourceFromFileName(f.name);
    setFile(f);
    setSource(src);
    setColumnMap(EMPTY_MAP);
    void runPreview(f, src, null);
  }, [runPreview]);

  const setMapField = useCallback((field: keyof TrialBalanceColumnMap, value: number) => {
    if (!file) return;
    const next = { ...columnMap, [field]: value };
    setColumnMap(next);
    void runPreview(file, source, next);
  }, [columnMap, file, source, runPreview]);

  const columnOptions = useMemo(() => [
    { value: '-1', label: '— Not present —' },
    ...(preview?.columns ?? []).map(c => ({ value: String(c.index), label: c.name || `Column ${c.index + 1}` })),
  ], [preview]);

  const hasMappingUi = source !== 'TALLY_XML' && (preview?.columns?.length ?? 0) > 0;
  const hasErrors = (preview?.diagnostics ?? []).some(d => d.severity === 'ERROR');
  const canUpload = !!file && !!preview && !hasErrors && (preview.rowCount ?? 0) > 0 && !previewing;

  const handleUpload = useCallback(async () => {
    if (!file || !canUpload) return;
    setUploading(true);
    try {
      const r = await fsaObVerificationService.upload(
        engagementId, source, file, hasMappingUi ? columnMap : null);
      if (r.data) onUploaded(r.data);
      notificationService.toast.success(`"${file.name}" uploaded — ${r.data?.header.rowCount ?? 0} ledgers compared.`);
      onClose();
    } catch (err: any) {
      notificationService.toast.error(err?.response?.data?.message ?? 'Error uploading the opening TB.');
    } finally {
      setUploading(false);
    }
  }, [file, canUpload, engagementId, source, columnMap, hasMappingUi, onUploaded, onClose]);

  if (!isOpen) return null;

  const MapSelect: React.FC<{ label: React.ReactNode; field: keyof TrialBalanceColumnMap }> = ({ label, field }) => (
    <div className="form-group">
      <SyutSelect
        label={label}
        value={String(columnMap[field])}
        onChange={(v) => setMapField(field, parseInt((v as string) || '-1', 10))}
        options={columnOptions}
      />
    </div>
  );

  return (
    <>
      <div className="modal-backdrop" onClick={onClose} />
      <div className="modal-container modal-lg" style={{ display: 'flex', flexDirection: 'column', maxHeight: '90vh' }}>
        <div className="modal-header" style={{ minHeight: 'auto' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            <IconSystem.upload size={16} />
            <h2 className="modal-title" style={{ margin: 0 }}>Upload Client Opening TB</h2>
          </div>
          <button className="modal-close" onClick={onClose}>
            <IconSystem.x size={20} />
          </button>
        </div>

        <div className="modal-content" style={{ flex: 1, overflow: 'auto' }}>
          {/* file picker */}
          <div className="form-group">
            <label className="form-label">Opening TB File <span className="text-danger">*</span></label>
            <input
              ref={fileInputRef}
              type="file"
              className="form-input"
              accept=".xlsx,.xls,.csv,.txt,.xml"
              onChange={(e) => handleFileChosen(e.target.files?.[0] ?? null)}
            />
            <div style={hintStyle}>
              The client's current-year opening trial balance (Excel / CSV / Tally XML). Used for
              verification only — it never changes this engagement's own opening figures.
            </div>
          </div>

          {previewing && (
            <div style={{ padding: '0.75rem', fontSize: '0.8rem', color: 'var(--text-secondary)' }}>Parsing…</div>
          )}

          {preview && !previewing && (
            <>
              {/* column mapping (delimited/sheet sources only) */}
              {hasMappingUi && (
                <>
                  <div style={sectionLabelStyle}>Confirm Column Mapping</div>
                  <div className="form-grid-3">
                    <MapSelect label={<>Ledger Name <span className="text-danger">*</span></>} field="ledgerIndex" />
                    <MapSelect label="Parent Group" field="groupIndex" />
                    <MapSelect label="Amount (single balance)" field="amountIndex" />
                  </div>
                  <div className="form-grid-3">
                    <MapSelect label="Debit" field="debitIndex" />
                    <MapSelect label="Credit" field="creditIndex" />
                    <MapSelect label="Opening (if separate)" field="openingIndex" />
                  </div>
                  <div style={hintStyle}>
                    Map either a single Amount column or Debit + Credit. When the file carries a
                    dedicated Opening column, map it — it takes precedence as the client's opening balance.
                  </div>
                </>
              )}

              {/* diagnostics */}
              {(preview.diagnostics ?? []).filter(d => d.severity === 'ERROR' || d.severity === 'WARN').slice(0, 4).map((d, i) => (
                <div key={i} style={{
                  ...hintStyle,
                  color: d.severity === 'ERROR' ? '#dc2626' : '#b45309',
                }}>
                  {d.severity === 'ERROR' ? '✕' : '⚠'} {d.message}
                </div>
              ))}

              {/* parsed summary + sample rows */}
              <div style={sectionLabelStyle}>
                Parsed Preview — {preview.rowCount} ledgers · Dr {fmt(preview.debitTotal)} · Cr {fmt(preview.creditTotal)} ·
                Difference {fmt(preview.difference)}
              </div>
              <div style={{ overflowX: 'auto', border: '1px solid var(--border-color)', borderRadius: 6 }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.75rem' }}>
                  <thead>
                    <tr>
                      {['Ledger', 'Parent Group', 'Amount'].map((h, i) => (
                        <th key={h} style={{ ...previewThStyle, textAlign: i === 2 ? 'right' : 'left' }}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {preview.rows.slice(0, 8).map((r, i) => (
                      <tr key={i}>
                        <td style={previewTdStyle}>{r.ledgerName}</td>
                        <td style={{ ...previewTdStyle, color: 'var(--text-secondary)' }}>{r.parentGroup ?? '—'}</td>
                        <td style={{ ...previewTdStyle, textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>
                          {fmt(r.openingAmount !== 0 ? r.openingAmount : r.closingAmount)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {preview.rowCount > 8 && (
                  <div style={{ padding: '4px 10px', fontSize: '0.68rem', color: 'var(--text-secondary)' }}>
                    + {preview.rowCount - 8} more ledgers…
                  </div>
                )}
              </div>
            </>
          )}
        </div>

        <div className="modal-footer" style={{ minHeight: 'auto' }}>
          <button className="btn btn-secondary" onClick={onClose} disabled={uploading}>Cancel</button>
          <button className="btn btn-primary" onClick={handleUpload} disabled={!canUpload || uploading}>
            <IconSystem.upload size={16} /> {uploading ? 'Uploading…' : 'Upload & Compare'}
          </button>
        </div>
      </div>
    </>
  );
};

const hintStyle: React.CSSProperties = {
  fontSize: '0.72rem',
  color: 'var(--text-secondary)',
  marginTop: 4,
  lineHeight: 1.4,
};
const sectionLabelStyle: React.CSSProperties = {
  fontSize: '0.7rem',
  fontWeight: 700,
  textTransform: 'uppercase',
  letterSpacing: 0.5,
  color: 'var(--text-secondary)',
  margin: '0.9rem 0 0.4rem',
};
const previewThStyle: React.CSSProperties = {
  padding: '5px 10px',
  borderBottom: '2px solid var(--border-color)',
  background: 'var(--bg-secondary)',
  fontSize: '0.65rem', textTransform: 'uppercase', letterSpacing: 0.4,
  color: 'var(--text-secondary)',
};
const previewTdStyle: React.CSSProperties = {
  padding: '4px 10px',
  borderBottom: '1px solid var(--border-color)',
  color: 'var(--text-primary)',
};

export default FsaObUploadModal;
